import { useContext, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import { SelectOption } from 'components/Select'
import { WorkspaceBidderContext } from 'features/globalSetup/contexts/WorkspaceBidderContext'
import { CommonForm } from 'features/globalSetup/forms/Prebid/Bidders/CommonForm'
import { DEFAULT_CPM_ADJUSTMENT } from './constants'
import { AddSchema, addSchemaDefault } from './schemas/addSchema'
import { CommonThirdPartyProviderSchema } from './schemas/commonSchema'
import { useGetBiddersForSelect } from './sections/hooks/useGetBiddersForSelect'
import { BidderSlice } from './types'

export const AddBidderFormBody = (): React.JSX.Element => {
  const { clearErrors, reset, setValue, trigger, watch } = useFormContext<AddSchema>()
  const { setAccountType } = useContext(WorkspaceBidderContext)

  const formValues = watch()
  const { accountType, bidderId } = formValues

  const bidders: SelectOption<BidderSlice>[] = useGetBiddersForSelect(accountType)

  const resetAfterAccountTypeChanged = (): void => {
    setAccountType(accountType)
    reset({ ...addSchemaDefault, accountType } as AddSchema)
  }

  const setFieldsDependingOnAccountType = (): void => {
    switch (accountType) {
      case 'own': {
        setValue('bidCpmAdjustment', DEFAULT_CPM_ADJUSTMENT)
        break
      }

      case 'thirdPartyProvider': {
        setValue('supplyChain', (formValues as CommonThirdPartyProviderSchema)['supplyChain'])
        break
      }

      case 'yieldbird': {
        setValue('status', 'requested')
        setValue('bidCpmAdjustment', DEFAULT_CPM_ADJUSTMENT)
        break
      }
    }
  }

  const setFieldsDependingOnBidderId = (): void => {
    if (bidderId?.rawData === undefined) return
    const { code, ybSid } = bidderId.rawData

    setValue('bidderCode', code)
    setValue('bidderSid', accountType === 'yieldbird' ? (ybSid ?? '') : '')
    trigger('bidderId') // https://github.com/react-hook-form/react-hook-form/issues/2755
  }

  useEffect(() => {
    resetAfterAccountTypeChanged()
    setFieldsDependingOnAccountType()
  }, [accountType])

  useEffect(() => {
    setFieldsDependingOnBidderId()
    clearErrors() // clear errors before 'bidderSid' validation
  }, [bidderId.value])

  return (
    <CommonForm
      bidders={bidders}
      variant='add'
    />
  )
}
